/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import { isInDevMode } from '@/utils/environment';
import store from '@/store';
import { CHAT, USER } from '@/store/types';
import * as api from '@/api/chat';
import { TIMEvent, TIMMessage, TIMConversation } from '@/api/types/chat';
import blockModule, { StoreChatUserBlockState } from './block';

export interface StoreChatUserState {
  block: StoreChatUserBlockState;
  online: boolean;
  nickname: string;
}

export default {
  namespaced: true,
  modules: {
    block: blockModule,
  },
  state: {
    online: false,
    nickname: null,
  },
  actions: {
    [CHAT.USER_LOGIN]({ state, commit }, { appid, uid, sig, silent }) {
      if (!state.online) {
        return new Promise((resolve, reject) => {
          api.getTIMSDK().then((TIM) => {
            let ready = false;
            api.login({
              appid,
              userSig: sig,
              userID: uid,
              logLevel: isInDevMode('chat') ? 0 : 1,
              on: { // 监听事件，如：
                [TIM.EVENT.SDK_READY]: () => {
                  // 收到离线消息和会话列表同步完毕通知，接入侧可以调用 sendMessage 等需要鉴权的接口
                  // event.name - TIM.EVENT.SDK_READY
                  ready = true;
                  commit(CHAT.USER_LOGIN);
                  resolve();
                },
                [TIM.EVENT.MESSAGE_RECEIVED]: (event: TIMEvent<TIMMessage[]>) => {
                  // 收到推送的私聊、群聊、群提示、群系统通知的新消息，可通过遍历 event.data 获取消息列表数据并渲染到页面
                  // event.name - TIM.EVENT.MESSAGE_RECEIVED
                  // event.data - 存储 Message 对象的数组 - [Message]
                  event.data.forEach((msg) => {
                    // 插入会话历史列表
                    store.commit(`chat/conversation/detail/${CHAT.GET_HISTORY}`, {
                      status: 'success',
                      mode: 'append',
                      list: [msg],
                      cid: msg.conversationID,
                    });
                    // 获取 im 用户对应的海鳗数据
                    store.dispatch(`user/im/${USER.GET_IM_USER_INFO}`, {
                      silent,
                      uids: [msg.from],
                    });
                  });
                },
                [TIM.EVENT.CONVERSATION_LIST_UPDATED]: (event: TIMEvent<TIMConversation[]>) => {
                  // 收到会话列表更新通知，可通过遍历 event.data 获取会话列表数据并渲染到页面
                  // event.name - TIM.EVENT.CONVERSATION_LIST_UPDATED
                  // event.data - 存储 Conversation 对象的数组 - [Conversation]
                  store.dispatch(`user/im/${USER.GET_IM_USER_INFO}`, {
                    silent: true,
                    uids: event.data.map(p => p.userProfile.userID),
                  }).then(() => {
                    store.commit(`chat/conversation/list/${CHAT.GET_CONVERSATION_LIST}`, { list: event.data });
                  });
                },
                [TIM.EVENT.GROUP_LIST_UPDATED]: () => {
                  // 收到群组列表更新通知，可通过遍历 event.data 获取群组列表数据并渲染到页面
                  // event.name - TIM.EVENT.GROUP_LIST_UPDATED
                  // event.data - 存储 Group 对象的数组 - [Group]
                },
                [TIM.EVENT.GROUP_SYSTEM_NOTICE_RECEIVED]: () => {
                  // 收到新的群系统通知
                  // event.name - TIM.EVENT.GROUP_SYSTEM_NOTICE_RECEIVED
                  // event.data.type - 群系统通知的类型，详情请参见 GroupSystemNoticePayload 的 <a href="https://imsdk-1252463788.file.myqcloud.com/IM_DOC/Web/Message.html#.GroupSystemNoticePayload"> operationType 枚举值说明</a>
                  // event.data.message - Message 对象，可将 event.data.message.content 渲染到到页面
                },
                [TIM.EVENT.PROFILE_UPDATED]: () => {
                  // 收到自己或好友的资料变更通知
                  // event.name - TIM.EVENT.PROFILE_UPDATED
                  // event.data - 存储 Profile 对象的数组 - [Profile]
                },
                [TIM.EVENT.BLACKLIST_UPDATED]: (event) => {
                  // 收到黑名单列表更新通知
                  // event.name - TIM.EVENT.BLACKLIST_UPDATED
                  // event.data - 存储 userID 的数组 - [userID]
                  store.commit(`chat/user/block/${CHAT.GET_BLACK_LIST}`, { list: event.data });
                },
                [TIM.EVENT.ERROR]: (event) => {
                  // 收到 SDK 发生错误通知，可以获取错误码和错误信息
                  // event.name - TIM.EVENT.ERROR
                  // event.data.code - 错误码
                  // event.data.message - 错误信息
                  if (ready) {
                    return;
                  }
                  reject(event.data);
                },
                [TIM.EVENT.SDK_NOT_READY]: () => {
                  // 收到 SDK 进入 not ready 状态通知，此时 SDK 无法正常工作
                  // event.name - TIM.EVENT.SDK_NOT_READY
                  console.warn('TIM.EVENT.SDK_NOT_READY!');
                },
                [TIM.EVENT.KICKED_OUT]: () => {
                  // 收到被踢下线通知
                  // event.name - TIM.EVENT.KICKED_OUT
                  // event.data.type - 被踢下线的原因，例如 TIM.TYPES.KICKED_OUT_MULT_ACCOUNT 多账号登录被踢
                  commit(CHAT.USER_LOGOUT);
                },
              },
            }).catch(reject);
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
    [CHAT.USER_LOGOUT]({ state, commit }) {
      if (store.state.user.im.appid && state.online) {
        return new Promise((resolve, reject) => {
          api.logout(store.state.user.im.appid).then(() => {
            commit(CHAT.USER_LOGOUT);
            resolve();
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
  },
  mutations: {
    [CHAT.USER_LOGIN](state, { nickname = state.nickname } = {}) {
      state.online = true;
      state.nickname = nickname;
    },
    [CHAT.USER_LOGOUT](state) {
      state.online = false;
    },
  },
};
